import moment from "moment";
import numeral from "numeral";

import * as MonitoringAPI from "api/MonitoringAPI";

import * as setupReducer from "containers/Setup/reducer";
import * as projectReducer from "containers/Project/reducer";
import * as homeReducer from "containers/Home/reducer";
import * as nextMonitoringReducer from "containers/Next/Monitoring/NextMonitoringReducer";
import * as nextReducer from "containers/Next/NextReducer";
import * as adminReducer from "containers/Admin/reducer";

import { SegmentMetric } from "utils/optimizer/ProjectOptimizer";

import { FAIRNESS_OBJECTIVE } from "containers/Project/reducer";
import { ConfusionMatrix } from "utils/stats";

import { isEnabled } from "utils/featureFlags";

const UPDATE_FIELD = "monitoring/UPDATE_FIELD";
const DELETE_MONITOR = "monitoring/DELETE_MONITOR";
const UPDATE_MONITOR = "monitoring/UPDATE_MONITOR";
const RESET_MONITORING = "monitoring/RESET_MONITORING";

const AIBLE_VALUES = { "Rare-Value-Aible": true, "Blank-Aible": true };

const DEFAULT_OVERALL_LABEL = "Overall";

export const ALERT_TYPES = {
  TRANSACTION_COUNT: "Transaction Count",
  SUCCESS_RATE: "Success Rate",
  DATA_DRIFT: "Data Drift",
  MODEL_DRIFT: "Model Drift"
};

export const MONITOR_VIEW_MODES = {
  IMPACT: "Impact",
  USER_BEHAVIOR: "User Behavior",
  DATA_EVALUATION: "Data Evaluation",
  USER_COMPLIANCE: "User Compliance",
  FAIRNESS: "Monitoring Fairness",
  TARGETING: "User Targeting",
  DATA_DRIFT: "Data and Model Drift"
};

export const NEXT_VIEW_MODES_LIST = [
  {
    value: MONITOR_VIEW_MODES.IMPACT,
    label: "Business Performance Monitoring"
  },
  { value: MONITOR_VIEW_MODES.FAIRNESS, label: "Monitoring Fairness" },
  { value: MONITOR_VIEW_MODES.TARGETING, label: "Group Targeting" },
  { value: MONITOR_VIEW_MODES.DATA_DRIFT, label: "Data and Model Drift" }
];

export const TARGET_VALUE_MODES = {
  PROFIT: "profit",
  REVENUE: "revenue",
  COST: "cost",
  AVERAGE_DEVIATION: "average deviation",
  TOTAL_SUM: "total sum",
  AVERAGE_SUM: "average sum"
};

export const GRAPH_MODES = {
  CUMULATIVE: "Cumulative",
  DAILY: "Daily"
};

export const GRAPH_MODES_HIDE_CUMULATIVE = [
  TARGET_VALUE_MODES.AVERAGE_DEVIATION,
  TARGET_VALUE_MODES.TOTAL_SUM,
  TARGET_VALUE_MODES.AVERAGE_SUM
];

// Threshold percentage differences to show alert cards
// values should be between 0 and 1
const PERFORMANCE_ALERT_THRESHOLDS = {
  TRANSACTION_RATE: 0.05,
  CONVERSION_RATE: 0.05
};

// Monitoring is keyed by project id for ease of access
const initialState = {};

const DEFAULT_MONITOR = {
  showRightPanel: true,
  monitorViewMode: MONITOR_VIEW_MODES.IMPACT,
  userFeedback: [],
  data: {},
  isLoading: false,
  targetValue: TARGET_VALUE_MODES.PROFIT,
  graphMode: GRAPH_MODES.CUMULATIVE
};

export default function monitorReducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_MONITOR:
      return {
        ...state,
        [action.id]: action.data
      };
    case DELETE_MONITOR:
      const newState = {
        ...state
      };
      delete newState[action.id];
      return newState;
    case UPDATE_FIELD:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          [action.key]: action.data
        }
      };
    case RESET_MONITORING:
      return { ...initialState };
    default:
      return state;
  }
}

/* Selectors */
export function getIsMonitoring() {
  return isEnabled("monitoring");
}

export function getIsMonitoringDemo() {
  return false;
  return isEnabled("monitoring-demo");
}

export function getMonitoring(state) {
  return state.monitoring;
}

export function getMonitor(state, id) {
  return getMonitoring(state)[id] || {};
}

export function getMonitorViewMode(state, id) {
  return getMonitor(state, id).monitorViewMode;
}

export function getUserFeedback(state, id) {
  return getMonitor(state, id).userFeedback;
}

export function getData(state, id) {
  return getMonitor(state, id).data;
}

export function getUserData(state, id) {
  return (getMonitor(state, id) || {}).userMonitors;
}

export function getTargetValue(state, id) {
  const currTargetValue = (getMonitor(state, id) || {}).targetValue;

  const options = getTargetValueOptions(state, id);
  const retOption = options.find((o) => o.value === currTargetValue);
  if (retOption) {
    return currTargetValue;
  }

  return options[0].value ?? currTargetValue;
}

export function getTargetValueOptions(state, id) {
  const selectedRunId = projectReducer.getRunId(state, id);
  const setupLabels = setupReducer.getSetupLabels(state, selectedRunId);
  const isContinuous = projectReducer.getIsContinuous(state, id);

  const targetValueOptions = [
    { value: TARGET_VALUE_MODES.PROFIT, label: setupLabels.profitLabel },
    { value: TARGET_VALUE_MODES.REVENUE, label: setupLabels.revenueLabel },
    { value: TARGET_VALUE_MODES.COST, label: setupLabels.costLabel }
  ];

  if (isContinuous) {
    return [
      { value: TARGET_VALUE_MODES.AVERAGE_SUM, label: "Average Sum" },
      { value: TARGET_VALUE_MODES.TOTAL_SUM, label: "Total Sum" },
      { value: TARGET_VALUE_MODES.AVERAGE_DEVIATION, label: "Mean Absolute Error" },
      { value: TARGET_VALUE_MODES.COST, label: setupLabels.costLabel }
    ];
  }

  return targetValueOptions;
}

export function getGraphMode(state, id) {
  const targetValue = getTargetValue(state, id);
  if (GRAPH_MODES_HIDE_CUMULATIVE.includes(targetValue)) {
    return GRAPH_MODES.DAILY;
  }
  return (getMonitor(state, id) || {}).graphMode;
}

export function getGraphModeOptions(state, id) {
  const graphModeOptions = [
    { value: GRAPH_MODES.CUMULATIVE, label: "Cumulative" },
    { value: GRAPH_MODES.DAILY, label: "Daily" }
  ];
  return graphModeOptions;
}

export function getLatestData(state, id) {
  return getMonitor(state, id).latestData;
}

export function getShowRightPanel(state, id) {
  return getMonitor(state, id).showRightPanel;
}

export function getMonitorWarnings(state, id) {
  return getMonitor(state, id).warnings;
}

export function getMonitorSegmentWarnings(state, id, segmentId) {
  const monitorWarnings = getMonitorWarnings(state, id);
  return monitorWarnings && monitorWarnings[segmentId];
}

export function getLatestScenarios(state, id) {
  return getMonitor(state, id).latestScenario;
}

export function getLatestScenariosBySegment(state, id, segmentId) {
  const latestScenarios = getLatestScenarios(state, id);
  return latestScenarios && latestScenarios[segmentId];
}

export function getLatestProfitBySegment(state, id, segmentId) {
  const latestSegmentScenarios = getLatestScenariosBySegment(state, id, segmentId);
  return (
    latestSegmentScenarios && latestSegmentScenarios.selectedScenario && latestSegmentScenarios.selectedScenario.profit
  );
}

export function hasSufficientData(state, id) {
  const data = getData(state, id) || {};

  return Object.values(data).every((x) => x.hasSufficientData);
}

export function getIsLoading(state, id) {
  return getMonitor(state, id).isLoading;
}

/* Actions */

export function deleteMonitor(id) {
  return { type: DELETE_MONITOR, id };
}

export function updateMonitor(id, data) {
  return { type: UPDATE_MONITOR, data, id };
}

export function updateMonitorData(id, data) {
  return { type: UPDATE_FIELD, id, data, key: "data" };
}

export function updateMonitorViewMode(id, data) {
  return { type: UPDATE_FIELD, id, data, key: "monitorViewMode" };
}

export function updateUserFeedback(id, data) {
  return { type: UPDATE_FIELD, id, data, key: "userFeedback" };
}

export function updateShowRightPanel(id, data) {
  return { type: UPDATE_FIELD, id, data, key: "showRightPanel" };
}

export function resetMonitoring() {
  return { type: RESET_MONITORING };
}

export function updateIsLoading(id, data) {
  return { type: UPDATE_FIELD, id, data, key: "isLoading" };
}

export function updateTargetValue(id, data) {
  return { type: UPDATE_FIELD, id, data, key: "targetValue" };
}

export function updateGraphMode(id, data) {
  return { type: UPDATE_FIELD, id, data, key: "graphMode" };
}

/* Methods */
export function init(projectId) {
  return (dispatch, getState) => {
    return dispatch(fetchMonitor(projectId));
  };
}

export function deleteUserFeedback(projectId, userId) {
  return (dispatch, getState) => {
    const currFeedback = getUserFeedback(getState(), projectId);
    const retFeedback = currFeedback.filter((u) => u.id !== userId);
    dispatch(updateUserFeedback(projectId, retFeedback));
  };
}

// Assume Project has Splits and is Categorical
// Get array of segments with split column and value
// Get 2 latest users from user list
export function createMonitorDemoFetch(projectId) {
  return (dispatch, getState) => {
    const project = projectReducer.getProject(getState(), projectId) || {};
    const segments = project.segments || [];

    const retSegments = segments
      .map((s) => {
        if (!s?.conditions?.length) {
          return;
        }

        const condition = s?.conditions?.[0];

        return {
          id: s?.id,
          column: condition?.column,
          value: condition?.value
        };
      })
      .filter((s) => Boolean(s));

    const users = adminReducer.getUsers(getState()) || [];

    const datasetId = projectReducer.getDatasetId(getState(), projectId);
    const rankedFields = homeReducer.getRankedFields(getState(), datasetId);
    const fields = rankedFields
      .filter((f) => f.type === "categorical")
      .map((f) => {
        const retField = {
          label: f.label,
          values: f.data?.map((d) => d.value)
        };
        return retField;
      });

    const seenNames = {};
    const retUsers = users
      .filter((u) => {
        if (seenNames[u?.fullname]) {
          return false;
        }
        seenNames[u?.fullname] = true;
        return true;
      })
      .filter((u, i, uList) => {
        return i > uList.length - 3;
      });

    return MonitoringAPI.getMonitoringDemo(project, {
      segments: retSegments,
      users: retUsers,
      fields: fields
    });
  };
}

export function fetchMonitor(projectId) {
  return (dispatch, getState) => {
    dispatch(updateIsLoading(projectId, true));

    const project = projectReducer.getProject(getState(), projectId) || {};

    if (!projectReducer.getIsScenarioDeployed(getState(), projectId)) {
      dispatch(updateIsLoading(projectId, false));
      return Promise.resolve();
    }

    const fetchMonitorHelper = () => {
      if (
        getIsMonitoringDemo() &&
        !projectReducer.getIsContinuous(getState(), projectId) &&
        projectReducer.getHasSplits(getState(), projectId)
      ) {
        return dispatch(createMonitorDemoFetch(projectId));
      }
      return MonitoringAPI.getMonitoring(project);
    };

    return fetchMonitorHelper()
      .then((response) => {
        // const currTenant = loginReducer.getSelectedTenant(getState());
        // dispatch(adminReducer.updateUsersCall(currTenant));
        const retMonitor = parseMonitoring(getState(), projectId, response?.global);
        if (!retMonitor) {
          return Promise.resolve();
        }
        return Promise.resolve(response.user)
          .then((res) => {
            if (res) {
              const resUserMonitors = {};
              res.forEach((elem) => {
                const userId = elem && elem.user && elem.user.id;
                if (resUserMonitors[userId] === undefined) {
                  resUserMonitors[userId] = [];
                }
                resUserMonitors[userId].push(elem);
              });
              const userMonitors = {};
              Object.keys(resUserMonitors).forEach((userId) => {
                const currUserMonitor = parseMonitoring(
                  getState(),
                  projectId,
                  resUserMonitors[userId],
                  true,
                  retMonitor.timeDifference
                );
                userMonitors[userId] = currUserMonitor;
              });
              retMonitor.userMonitors = userMonitors;
            }
            if (retMonitor) {
              dispatch(updateMonitor(projectId, retMonitor));

              const nextId = nextReducer.getNextIdFromProjectId(getState(), projectId);
              dispatch(nextMonitoringReducer.checkMonitoringSummary(nextId));

              return retMonitor;
            } else {
              return dispatch(updateIsLoading(projectId, false));
            }
          })
          .catch((error) => {
            console.log(error);
            return dispatch(updateIsLoading(projectId, false));
          });
      })
      .catch((error) => {
        console.log(error);
        return dispatch(updateIsLoading(projectId, false));
      });
  };
}

function getDateFormatString(showHoursOverride = false) {
  if (!showHoursOverride) {
    return "MMM Do YYYY";
  } else {
    return "MMM Do YYYY h:mma";
  }
}

function formatDate(startDate, endDate, showHoursOverride = false) {
  const mStart = moment(startDate);
  const mEnd = moment(endDate);
  const formatString = getDateFormatString(showHoursOverride);
  return mStart.format(formatString);
}

// Helper function to eliminate NaNs from the ConfusionMatrix getPrecision Method
function getConversionRate(cm) {
  if (!cm) {
    return 0;
  }
  const tp = cm.getTruePositive();
  const positives = cm.getAllPositive();
  return tp / (positives || 1);
}

function generateMonitoringPoint(
  state,
  {
    currMonitor,
    currMonitorDataArr,
    currLabel,
    currDate,
    segmentConfusionMatrices,
    isContinuous,
    projectId,
    segmentId,
    assumptions,
    dateOffset = 0,
    timeDifference
  } = {}
) {
  const conditionDataArr = currMonitorDataArr.map((currMonitorData) => {
    return createMonitorConditionData(state, {
      currMonitor,
      currMonitorData: currMonitorData,
      currLabel,
      currDate,
      segmentConfusionMatrices,
      isContinuous,
      projectId,
      segmentId,
      assumptions,
      dateOffset,
      timeDifference
    });
  });

  const retMonitoringPoint = conditionDataArr.reduce((mpAcc, conditionData) => {
    if (mpAcc === undefined || !conditionData) {
      return conditionData;
    } else {
      const retDatapoint = { ...mpAcc };
      const sDateMoment = moment(mpAcc.startDate);
      const eDateMoment = moment(mpAcc.endDate);
      retDatapoint.date = mpAcc.date;
      retDatapoint.startDate = sDateMoment.isBefore(conditionData.startDate)
        ? mpAcc.startDate
        : conditionData.startDate;
      retDatapoint.endDate = eDateMoment.isAfter(conditionData.endDate) ? mpAcc.endDate : conditionData.endDate;

      retDatapoint.label = mpAcc.label ? mpAcc.label : conditionData.label;
      retDatapoint.count = mpAcc.count + conditionData.count;
      if (!mpAcc.rawData) {
        retDatapoint.rawData = conditionData.rawData;
      } else {
        retDatapoint.rawData = mpAcc.rawData;
        if (conditionData.rawData) {
          Object.keys(conditionData.rawData).forEach((colKey) => {
            if (conditionData.rawData[colKey]) {
              if (!retDatapoint.rawData[colKey]) {
                retDatapoint.rawData[colKey] = {};
              }
              Object.keys(conditionData.rawData[colKey]).forEach((valKey) => {
                if (!retDatapoint.rawData[colKey][valKey]) {
                  retDatapoint.rawData[colKey][valKey] = {};
                }
                if (conditionData.rawData[colKey][valKey]) {
                  Object.keys(conditionData.rawData[colKey][valKey]).forEach((dataKey) => {
                    const accData = retDatapoint.rawData[colKey][valKey][dataKey];
                    const currData = conditionData.rawData[colKey][valKey][dataKey];
                    const newValue = (accData || 0) + (currData || 0);
                    if (!isNaN(newValue)) {
                      retDatapoint.rawData[colKey][valKey][dataKey] = newValue;
                    }
                  });
                }
              });
            }
          });
        }
      }

      retDatapoint.countAssumed =
        mpAcc.countAssumed !== undefined
          ? mpAcc.countAssumed + (conditionData.countAssumed || 0)
          : conditionData.countAssumed;
      if (!isContinuous) {
        retDatapoint.assumedConfusionMatrix =
          mpAcc.assumedConfusionMatrix && conditionData.assumedConfusionMatrix
            ? mpAcc.assumedConfusionMatrix.combineMatrices(conditionData.assumedConfusionMatrix)
            : mpAcc.assumedConfusionMatrix || conditionData.assumedConfusionMatrix;
        retDatapoint.assumedConversionRate =
          retDatapoint.assumedConfusionMatrix && getConversionRate(retDatapoint.assumedConfusionMatrix);
        retDatapoint.assumedConversionRatePercentage = retDatapoint.assumedConversionRate * 100;

        retDatapoint.confusionMatrix =
          mpAcc.confusionMatrix && conditionData.confusionMatrix
            ? mpAcc.confusionMatrix.combineMatrices(conditionData.confusionMatrix)
            : mpAcc.confusionMatrix || conditionData.confusionMatrix;
        retDatapoint.conversionRate = retDatapoint.confusionMatrix && getConversionRate(retDatapoint.confusionMatrix);
        retDatapoint.conversionRatePercentage = retDatapoint.conversionRate * 100;

        retDatapoint.impact = mpAcc.impact + conditionData.impact;
        retDatapoint.averageImpact = retDatapoint.impact / (retDatapoint.count || 1);
      } else {
        retDatapoint.costAssumed =
          mpAcc.costAssumed !== undefined
            ? mpAcc.costAssumed + (conditionData.costAssumed || 0)
            : conditionData.costAssumed;

        retDatapoint.overCount = mpAcc.overCount + conditionData.overCount;
        retDatapoint.underCount = mpAcc.underCount + conditionData.underCount;

        retDatapoint.overSum = mpAcc.overSum + conditionData.overSum;
        retDatapoint.underSum = mpAcc.underSum + conditionData.underSum;

        retDatapoint.overCost = mpAcc.overCost + conditionData.overCost;
        retDatapoint.underCost = mpAcc.underCost + conditionData.underCost;

        retDatapoint.cost = retDatapoint.overCost + retDatapoint.underCost;
        retDatapoint.averageCost = retDatapoint.cost / (retDatapoint.overCount + retDatapoint.underCount || 1);

        retDatapoint.totalSum = mpAcc.totalSum + conditionData.totalSum;
        retDatapoint.totalSumAssumed = mpAcc.totalSumAssumed + conditionData.totalSumAssumed;
      }
      return retDatapoint;
    }
  }, undefined);

  if (isContinuous) {
    retMonitoringPoint.averageDeviation =
      (retMonitoringPoint.overSum * retMonitoringPoint.overCount +
        retMonitoringPoint.underSum * retMonitoringPoint.underCount) /
      (retMonitoringPoint.overSum + retMonitoringPoint.underSum || 1);
    retMonitoringPoint.averageSum =
      retMonitoringPoint.totalSum / (retMonitoringPoint.overCount + retMonitoringPoint.underCount || 1);
    retMonitoringPoint.averageSumAssumed =
      retMonitoringPoint.totalSumAssumed / (retMonitoringPoint.overCount + retMonitoringPoint.underCount || 1);
  }

  return retMonitoringPoint;
}

function createMonitorConditionData(
  state,
  {
    currMonitor,
    currMonitorData,
    currLabel,
    currDate,
    segmentConfusionMatrices,
    isContinuous,
    projectId,
    segmentId,
    assumptions,
    timeDifference = {}
  } = {}
) {
  const monitorVersion = getMonitorVersion(currMonitor);

  const currCount = currMonitorData[getMonitorKey("COUNT", monitorVersion)];

  let retMonitorPoint = {
    // raw: currMonitor,
    startDate: currMonitor.start_date,
    endDate: currMonitor.end_date,
    date: currDate,
    label: currLabel,
    count: !isNaN(currCount) ? currCount : currMonitorData[getMonitorKey("COUNT_", monitorVersion)],
    rawData: getMonitorDataPointer(currMonitor)
  };

  if (
    currMonitor.data &&
    currMonitor.data.compliance_results &&
    Object.keys(currMonitor.data.compliance_results).length > 0
  ) {
    retMonitorPoint.compliance = currMonitor.data.compliance_results;
  }

  let currMonitorPoint = {};

  if (!isContinuous) {
    const tp = currMonitorData[getMonitorKey("TRUE_POSITIVE", monitorVersion)];
    const fp = currMonitorData[getMonitorKey("FALSE_POSITIVE", monitorVersion)];
    const tn = currMonitorData[getMonitorKey("TRUE_NEGATIVE", monitorVersion)];
    const fn = currMonitorData[getMonitorKey("FALSE_NEGATIVE", monitorVersion)];

    const cm = new ConfusionMatrix(tp, tn, fp, fn);

    const rawAssumedCm = segmentConfusionMatrices && segmentConfusionMatrices[segmentId];

    const assumedCm = rawAssumedCm && rawAssumedCm.copyAndScaleTotal(rawAssumedCm.getTotal() * timeDifference.scalar);

    const countAssumed = assumedCm ? assumedCm.getTotal() : 0;
    const assumedConversionRate = assumedCm ? getConversionRate(assumedCm) : 0;

    const currConversionRate = getConversionRate(cm);
    const currImpact = projectReducer.getProfitFromMatrix(state, cm, undefined, assumptions);

    const currCost = projectReducer.getCostFromMatrix(state, cm, undefined, assumptions);

    const currRevenue = projectReducer.getRevenueFromMatrix(state, cm, undefined, assumptions);
    const avgCurrImpact = currImpact / (cm.getTotal() || 1);
    const avgCurrCost = currCost / (cm.getTotal() || 1);
    const avgCurrRevenue = currRevenue / (cm.getTotal() || 1);

    currMonitorPoint = {
      confusionMatrix: cm,
      assumedConfusionMatrix: assumedCm,
      countAssumed: countAssumed,
      conversionRate: currConversionRate,
      assumedConversionRate: assumedConversionRate,
      conversionRatePercentage: currConversionRate * 100,
      assumedConversionRatePercentage: assumedConversionRate * 100,
      impact: currImpact,
      profit: currImpact,
      averageProfit: avgCurrImpact,
      cost: currCost,
      revenue: currRevenue,
      averageImpact: avgCurrImpact,
      averageCost: avgCurrCost,
      averageRevenue: avgCurrRevenue
    };
  } else {
    const overSum = Math.abs(currMonitorData[getMonitorKey("OVER_SUM", monitorVersion)]);
    const underSum = Math.abs(currMonitorData[getMonitorKey("UNDER_SUM", monitorVersion)]);

    const overCount = currMonitorData[getMonitorKey("OVER_COUNT", monitorVersion)];
    const underCount = currMonitorData[getMonitorKey("UNDER_COUNT", monitorVersion)];

    const totalSum = currMonitorData[getMonitorKey("ACTUAL_SUM", monitorVersion)];
    const totalSumAssumed = currMonitorData[getMonitorKey("PREDICTED_SUM", monitorVersion)];

    const averageDeviation = (overSum * overCount + underSum * underCount) / (overSum + underSum || 1);

    const overEstimateCost = assumptions.conversionGain;
    const underEstimateCost = assumptions.conversionCost;

    const overCost = overSum * Math.abs(overEstimateCost);
    const underCost = underSum * Math.abs(underEstimateCost);
    const overUnderCost = overCost + underCost;
    const overUnderCount = overCount + underCount;

    const segment = projectReducer.getSegment(state, projectId, segmentId);
    const runId = segment?.job?.job_id;

    const expectedValueContinuous = setupReducer.getExpectedValueContinuous(state, runId, projectId) ?? 0;
    const expectedContinuousPercent = setupReducer.getCurrentContinuousPercent(state, runId, projectId) ?? 1;

    const cost = overUnderCount * expectedValueContinuous - overUnderCost;

    // const avgCost = (overSum * Math.abs(overEstimateCost) / (overCount || 1)) + (underSum * Math.abs(underEstimateCost) / (underCount || 1));
    const avgCost = cost / (overUnderCount || 1);

    const runModel = projectReducer.getBestModel(state, projectId, runId);
    const assumedOverCount = Math.round(runModel?.metrics?.count_over_predict * timeDifference.scalar);
    const assumedUnderCount = Math.round(runModel?.metrics?.count_under_predict * timeDifference.scalar);
    const assumedOverSum = runModel?.metrics?.sum_over_predict * timeDifference.scalar;
    const assumedUnderSum = runModel?.metrics?.sum_under_predict * timeDifference.scalar;

    const assumedOverCost = assumedOverSum * Math.abs(overEstimateCost);
    const assumedUnderCost = assumedUnderSum * Math.abs(underEstimateCost);
    const assumedOverUnderCost = assumedOverCost + assumedUnderCost;

    let expectedCountContinuous = assumptions.isRealityHammer
      ? assumptions.expectedCountContinuous
      : assumptions.baselineCountContinuous;
    if (expectedCountContinuous === undefined) {
      expectedCountContinuous = setupReducer.getExpectedCountContinuous(state, runId, projectId) ?? 0;
    }
    const countAssumed = Math.round(expectedCountContinuous * timeDifference.scalar);

    const costAssumed = countAssumed * expectedValueContinuous * expectedContinuousPercent - assumedOverUnderCost;

    currMonitorPoint = {
      countAssumed: countAssumed,
      costAssumed: costAssumed,
      overCount: overCount,
      underCount: underCount,
      overSum: overSum,
      underSum: underSum,
      cost: cost,
      overCost: overCost,
      underCost: underCost,
      averageCost: avgCost,
      averageDeviation: averageDeviation,
      totalSum: totalSum,
      totalSumAssumed: totalSumAssumed
    };
  }
  return {
    ...retMonitorPoint,
    ...currMonitorPoint
  };
}

function findWithOffset(array, checkFunction, offset = 0) {
  let retElem = undefined;
  let elemsFound = 0;
  for (let i = 0; i < array.length && elemsFound <= offset; i++) {
    const currElem = array[i];
    if (checkFunction(currElem)) {
      retElem = currElem;
      elemsFound++;
    }
  }
  return retElem;
}

function generateOverallMonitoringPoint({
  date,
  overallMonitorPoint,
  projectSegments,
  otherSegments,
  monitorData,
  overallSegmentId,
  isContinuous,
  dateOffset = 0
} = {}) {
  const defaultMonitorPoint = {
    ...overallMonitorPoint,
    date: date,
    label: projectSegments[overallSegmentId].name || DEFAULT_OVERALL_LABEL
  };
  let currMonitorPoint = {};
  if (!isContinuous) {
    let currCount = 0;
    let currTotal = 0;
    let countAssumed = 0;
    let currConversionRate = 0;
    let assumedConversionRate = 0;
    let currImpact = 0;
    let avgCurrImpact = 0;
    let currRevenue = 0;
    let avgCurrRevenue = 0;
    let currCost = 0;
    let avgCurrCost = 0;
    let combinedCm;
    let combinedAssumeCm;

    let combinedCompliance;

    let rawData = {};

    otherSegments.forEach((currSegmentId) => {
      const currDataPoint = findWithOffset(monitorData[currSegmentId].data, (elem) => elem.date === date, dateOffset);
      if (!currDataPoint) {
        return;
      }

      const cRawData = currDataPoint.rawData;
      for (const [cCol, cRData] of Object.entries(cRawData ?? {})) {
        if (!rawData[cCol]) {
          rawData[cCol] = {};
        }
        for (const [cVal, cRDataVal] of Object.entries(cRData ?? {})) {
          if (!rawData[cCol][cVal]) {
            rawData[cCol][cVal] = {};
          }
          for (const [cVKey, cRDataValData] of Object.entries(cRDataVal ?? {})) {
            if (!rawData[cCol][cVal][cVKey]) {
              rawData[cCol][cVal][cVKey] = 0;
            }
            rawData[cCol][cVal][cVKey] += cRDataValData;
          }
        }
      }

      currCount += currDataPoint.count;
      countAssumed += currDataPoint.countAssumed;
      if (combinedCm) {
        combinedCm.combineMatrices(currDataPoint.confusionMatrix);
      } else {
        combinedCm = currDataPoint.confusionMatrix;
      }
      if (combinedAssumeCm) {
        combinedAssumeCm.combineMatrices(currDataPoint.assumedConfusionMatrix);
      } else {
        combinedAssumeCm = currDataPoint.assumedConfusionMatrix;
      }
      currImpact += currDataPoint.impact;
      avgCurrImpact =
        (avgCurrImpact * currTotal + currDataPoint.impact) /
        (currTotal + currDataPoint.confusionMatrix.getTotal() || 1);
      currRevenue += currDataPoint.revenue;
      avgCurrRevenue =
        (avgCurrImpact * currTotal + currDataPoint.revenue) /
        (currTotal + currDataPoint.confusionMatrix.getTotal() || 1);
      currCost += currDataPoint.cost;
      avgCurrCost =
        (avgCurrImpact * currTotal + currDataPoint.cost) / (currTotal + currDataPoint.confusionMatrix.getTotal() || 1);

      currTotal += currDataPoint.confusionMatrix.getTotal();

      if (currDataPoint.compliance) {
        if (!combinedCompliance) {
          combinedCompliance = {};
        }
        Object.keys(currDataPoint.compliance).forEach((cKey) => {
          let accValue = combinedCompliance[cKey];
          if (currDataPoint.compliance[cKey] !== undefined) {
            combinedCompliance[cKey] = (accValue || 0) + (currDataPoint.compliance[cKey] || 0);
          }
        });
      }
    });
    currConversionRate = combinedCm ? getConversionRate(combinedCm) : 0;
    if (isNaN(currConversionRate)) {
      currConversionRate = 0;
    }
    assumedConversionRate = combinedAssumeCm ? getConversionRate(combinedAssumeCm) : 0;
    if (isNaN(assumedConversionRate)) {
      assumedConversionRate = 0;
    }

    currMonitorPoint = {
      count: currCount,
      confusionMatrix: combinedCm,
      assumedConfusionMatrix: combinedAssumeCm,
      countAssumed: countAssumed,
      conversionRate: currConversionRate,
      assumedConversionRate: assumedConversionRate,
      conversionRatePercentage: currConversionRate * 100,
      assumedConversionRatePercentage: assumedConversionRate * 100,
      impact: currImpact,
      profit: currImpact,
      cost: currCost,
      revenue: currRevenue,
      averageImpact: avgCurrImpact,
      averageProfit: avgCurrImpact,
      averageCost: avgCurrCost,
      averageRevenue: avgCurrRevenue,
      compliance: combinedCompliance,
      rawData: rawData
    };
  } else {
    let currCount = 0;
    let currOverCount = 0;
    let currUnderCount = 0;
    let currOverSum = 0;
    let currUnderSum = 0;
    let currTotal = 0;
    let avgCurrCost = 0;
    let currCost = 0;
    let currOverCost = 0;
    let currUnderCost = 0;
    let countAssumed = undefined;
    let costAssumed = undefined;
    let rawData = {};
    let currTotalSum = 0;
    let currTotalSumAssumed = 0;

    let averageDeviationNumerator = 0;
    let averageDeviationDenominator = 0;

    let combinedCompliance;

    otherSegments.forEach((currSegmentId) => {
      const currDataPoint = monitorData[currSegmentId].data.find((elem) => elem.date === date);
      if (!currDataPoint) {
        return;
      }

      const cRawData = currDataPoint.rawData;
      for (const [cCol, cRData] of Object.entries(cRawData ?? {})) {
        if (!rawData[cCol]) {
          rawData[cCol] = {};
        }
        for (const [cVal, cRDataVal] of Object.entries(cRData ?? {})) {
          if (!rawData[cCol][cVal]) {
            rawData[cCol][cVal] = {};
          }
          for (const [cVKey, cRDataValData] of Object.entries(cRDataVal ?? {})) {
            if (!rawData[cCol][cVal][cVKey]) {
              rawData[cCol][cVal][cVKey] = 0;
            }
            rawData[cCol][cVal][cVKey] += cRDataValData;
          }
        }
      }

      currCount += currDataPoint.count;
      if (currDataPoint.countAssumed !== undefined) {
        if (countAssumed !== undefined) {
          countAssumed += currDataPoint.countAssumed;
        } else {
          countAssumed = currDataPoint.countAssumed;
        }
      }
      if (currDataPoint.costAssumed !== undefined) {
        if (costAssumed !== undefined) {
          costAssumed += currDataPoint.costAssumed;
        } else {
          costAssumed = currDataPoint.costAssumed;
        }
      }
      currCost += currDataPoint.cost;
      currOverCost += currDataPoint.overCost;
      currUnderCost += currDataPoint.underCost;
      currOverSum += currDataPoint.overSum;
      currUnderSum += currDataPoint.underSum;
      currTotalSum += currDataPoint.totalSum;
      currTotalSumAssumed += currDataPoint.totalSumAssumed;
      const currOverUnderCount = currDataPoint.overCount + currDataPoint.underCount;
      avgCurrCost = (avgCurrCost * currTotal + currDataPoint.cost) / (currTotal + currOverUnderCount || 1);
      currTotal += currOverUnderCount;
      currOverCount += currDataPoint.overCount;
      currUnderCount += currDataPoint.underCount;

      averageDeviationNumerator +=
        currDataPoint.overCost * currDataPoint.overCount + currDataPoint.underCost * currDataPoint.underCount;
      averageDeviationDenominator += currDataPoint.overSum + currDataPoint.underSum;

      if (currDataPoint.compliance) {
        if (!combinedCompliance) {
          combinedCompliance = {};
        }
        Object.keys(currDataPoint.compliance).forEach((cKey) => {
          let accValue = combinedCompliance[cKey];
          if (currDataPoint.compliance[cKey] !== undefined) {
            combinedCompliance[cKey] = (accValue || 0) + (currDataPoint.compliance[cKey] || 0);
          }
        });
      }
    });

    currMonitorPoint = {
      count: currCount,
      overCount: currOverCount,
      underCount: currUnderCount,
      underSum: currUnderSum,
      overSum: currOverSum,
      countAssumed: countAssumed,
      costAssumed: costAssumed,
      cost: currCost,
      averageDeviation: averageDeviationNumerator / (averageDeviationDenominator || 1),
      totalSum: currTotalSum,
      totalSumAssumed: currTotalSumAssumed,
      averageSum: currTotalSum / (currCount || 1),
      averageSumAssumed: currTotalSumAssumed / (currCount || 1),
      underCost: currUnderCost,
      overCost: currOverCost,
      averageCost: avgCurrCost,
      compliance: combinedCompliance,
      rawData: rawData
    };
  }
  return {
    ...defaultMonitorPoint,
    ...currMonitorPoint
  };
}

function generateScenarioPoint({
  state,
  date,
  currSegmentData,
  scenarioData,
  rawOriginalScenarios,
  scenarioIndex,
  isContinuous,
  timeDifference,
  segmentId,
  projectId
} = {}) {
  let selectedScenario, bestScenario, worstScenario;
  let retScenarios, sortedScenarios;

  if (!currSegmentData) {
    return undefined;
  }

  if (!isContinuous) {
    const currSegmentCm = currSegmentData.confusionMatrix;
    const currAssumedSegmentCm = currSegmentData.assumedConfusionMatrix;

    retScenarios = scenarioData.map((currScenario, j) => {
      const currOriginalScenario = rawOriginalScenarios[j];

      const currSetup = currScenario.setup;
      const currRunId = currScenario.runId;
      const currScenId = currOriginalScenario.id + 1;

      const currName = currOriginalScenario.name ? currOriginalScenario.name : "Scenario " + currScenId;

      const currProfit = projectReducer.getProfitFromMatrix(state, currSegmentCm, currRunId, currSetup);
      const currRevenue = projectReducer.getRevenueFromMatrix(state, currSegmentCm, currRunId);
      const currCost = projectReducer.getCostFromMatrix(state, currSegmentCm, currRunId);

      const currAssumedProfit = projectReducer.getProfitFromMatrix(state, currAssumedSegmentCm, currRunId, currSetup);
      const currAssumedRevenue = projectReducer.getRevenueFromMatrix(state, currAssumedSegmentCm, currRunId);
      const currAssumedCost = projectReducer.getCostFromMatrix(state, currAssumedSegmentCm, currRunId);

      const retScenarioData = {
        // rawOriginalScenario: currOriginalScenario,
        // rawScenario: currScenario,
        date: date,
        name: currName,
        index: j,
        isSelected: j === scenarioIndex,
        profit: currProfit,
        revenue: currRevenue,
        cost: currCost,
        profitAssumed: currAssumedProfit,
        revenueAssumed: currAssumedRevenue,
        costAssumed: currAssumedCost
      };

      if (j === scenarioIndex) {
        selectedScenario = retScenarioData;
      }

      return retScenarioData;
    });
  } else {
    retScenarios = scenarioData.map((currScenario, j) => {
      const currOriginalScenario = rawOriginalScenarios[j];

      const currSetup = currScenario.setup;
      const currRunId = currScenario.runId;
      const currScenId = currOriginalScenario.id + 1;

      const segment = projectReducer.getSegment(state, projectId, segmentId);
      const runId = currRunId ?? segment?.job?.job_id;

      const currName = currOriginalScenario.name
        ? currOriginalScenario.name + "(" + currScenId + ")"
        : "Scenario " + currScenId;

      const overEstimateCost = currSetup?.data?.conversionGain ?? 1;
      const underEstimateCost = currSetup?.data?.conversionCost ?? 1;

      const underCost = currSegmentData.underSum * Math.abs(underEstimateCost);
      const overCost = currSegmentData.overSum * Math.abs(overEstimateCost);

      const count = currSegmentData.underCount + currSegmentData.overCount;
      let avgValue = currSetup.data.isRealityHammer
        ? currSetup.data.expectedValueContinuous
        : currSetup.data.baselineValueContinuous;
      if (avgValue === undefined) {
        avgValue = setupReducer.getExpectedValueContinuous(state, runId, projectId) ?? 0;
      }
      let totalAssumedCount = currSetup.data.isRealityHammer
        ? currSetup.data.expectedCountContinuous
        : currSetup.data.baselineCountContinuous;
      if (totalAssumedCount === undefined) {
        totalAssumedCount = setupReducer.getExpectedCountContinuous(state, runId, projectId) ?? 0;
      }
      const countAssumed = Math.round(totalAssumedCount * timeDifference.scalar);

      let assumedContinuousPercent = currSetup.data.continuousPercent;
      if (assumedContinuousPercent === undefined) {
        assumedContinuousPercent = setupReducer.getCurrentContinuousPercent(state, runId, projectId) ?? 1;
      }

      const runModel = projectReducer.getBestModel(state, projectId, runId);
      const assumedOverCount = Math.round(runModel?.metrics?.count_over_predict * timeDifference.scalar);
      const assumedUnderCount = Math.round(runModel?.metrics?.count_under_predict * timeDifference.scalar);
      const assumedOverSum = runModel?.metrics?.sum_over_predict * timeDifference.scalar;
      const assumedUnderSum = runModel?.metrics?.sum_under_predict * timeDifference.scalar;

      const assumedOverCost = assumedOverSum * Math.abs(overEstimateCost);
      const assumedUnderCost = assumedUnderSum * Math.abs(underEstimateCost);
      const assumedOverUnderCost = assumedOverCost + assumedUnderCost;

      const costAssumed = countAssumed * avgValue * assumedContinuousPercent - assumedOverUnderCost;
      const cost = count * avgValue - (overCost + underCost);

      const retScenarioData = {
        // rawOriginalScenario: currOriginalScenario,
        // rawScenario: currScenario,
        date: date,
        name: currName,
        index: j,
        isSelected: j === scenarioIndex,
        cost: cost,
        count: count,
        underCost: underCost,
        overCost: overCost,
        underCount: currSegmentData.underCount,
        overCount: currSegmentData.overCount,
        costAssumed: costAssumed,
        countAssumed: countAssumed,
        averageDeviation: currSegmentData.averageDeviation,
        totalSum: currSegmentData.totalSum,
        totalSumAssumed: currSegmentData.totalSumAssumed,
        averageSum: currSegmentData.averageSum,
        averageSumAssumed: currSegmentData.averageSumAssumed
      };

      if (j === scenarioIndex) {
        selectedScenario = retScenarioData;
      }

      return retScenarioData;
    });
  }

  sortedScenarios = [...retScenarios].sort((a, b) => (isContinuous ? a.cost - b.cost : b.profit - a.profit));
  bestScenario = sortedScenarios[0];
  worstScenario = sortedScenarios[sortedScenarios.length - 1];

  const currScenarioPoint = {
    date: date,
    scenarios: retScenarios,
    sortedScenarios: sortedScenarios,
    selectedScenario: selectedScenario,
    bestScenario: bestScenario,
    worstScenario: worstScenario
  };
  return currScenarioPoint;
}

function generateOverallScenarioPoint({
  otherSegments,
  monitorData,
  date,
  scenarioIndex,
  isContinuous,
  currOverallScenarioPoint,
  dateOffset = 0,
  overallSegmentId
} = {}) {
  const currScenarios = [];

  otherSegments.forEach((currSegmentId) => {
    const currScenarioPoint = findWithOffset(monitorData[currSegmentId].scenarios, (d) => d.date === date, dateOffset);
    if (currScenarioPoint && currScenarioPoint.scenarios) {
      currScenarioPoint.scenarios.forEach((scenario, j) => {
        if (currScenarios[j]) {
          const tempScenario = currScenarios[j];
          let retScenario = {};
          if (!isContinuous) {
            const profitAssumed = scenario?.profitAssumed ?? 0;
            const costAssumed = scenario?.costAssumed ?? 0;
            const revenueAssumed = scenario?.revenueAssumed ?? 0;

            const profit = scenario?.profit ?? 0;
            const cost = scenario?.cost ?? 0;
            const revenue = scenario?.revenue ?? 0;
            retScenario = {
              profit: tempScenario.profit + profit,
              revenue: tempScenario.revenue + revenue,
              cost: tempScenario.cost + cost,
              profitAssumed: tempScenario.profitAssumed + profitAssumed,
              revenueAssumed: tempScenario.revenueAssumed + revenueAssumed,
              costAssumed: tempScenario.costAssumed + costAssumed
            };
          } else {
            const count = scenario?.count ?? 0;
            const cost = scenario?.cost ?? 0;
            const countAssumed = scenario?.countAssumed ?? 0;
            const costAssumed = scenario?.costAssumed ?? 0;
            const underCost = scenario?.underCost ?? 0;
            const underCount = scenario?.underCount ?? 0;
            const overCost = scenario?.overCost ?? 0;
            const overCount = scenario?.overCount ?? 0;

            retScenario = {
              count: tempScenario.count + count,
              cost: tempScenario.cost + cost,
              countAssumed: tempScenario.countAssumed + countAssumed,
              costAssumed: tempScenario.costAssumed + costAssumed,
              underCost: tempScenario.underCost + underCost,
              underCount: tempScenario.underCount + underCount,
              overCost: tempScenario.overCost + overCost,
              overCount: tempScenario.overCount + overCount
            };
          }
          currScenarios[j] = {
            ...tempScenario,
            ...retScenario
          };
        } else {
          currScenarios[j] = {
            ...scenario,
            averageDeviation:
              currOverallScenarioPoint?.selectedScenario?.averageDeviation ?? scenario?.averageDeviation,
            totalSum: currOverallScenarioPoint?.selectedScenario?.totalSum ?? scenario?.totalSum,
            totalSumAssumed: currOverallScenarioPoint?.selectedScenario?.totalSumAssumed ?? scenario?.totalSumAssumed,
            averageSum: currOverallScenarioPoint?.selectedScenario?.averageSum ?? scenario?.averageSum,
            averageSumAssumed:
              currOverallScenarioPoint?.selectedScenario?.averageSumAssumed ?? scenario?.averageSumAssumed
          };
        }
      });
    }
  });

  const sortedScenarios = [...currScenarios].sort((a, b) => (isContinuous ? a.cost - b.cost : b.profit - a.profit));
  const bestScenario = sortedScenarios[0];
  const worstScenario = sortedScenarios[sortedScenarios.length - 1];

  let overallScenarioPoint = {
    ...currOverallScenarioPoint,
    date: date,
    scenarios: currScenarios,
    sortedScenarios: sortedScenarios,
    selectedScenario: currScenarios[scenarioIndex],
    bestScenario: bestScenario,
    worstScenario: worstScenario
  };
  return overallScenarioPoint;
}

function generateMonitoringWarnings(
  state,
  projectId,
  segmentId,
  { currLatestData, timeDifference, isContinuous } = {}
) {
  const jobId = projectReducer.getSegmentRunId(state, projectId, segmentId);
  const setupLabels = setupReducer.getSetupLabels(state, jobId);

  const retMonitorWarnings = [];
  const cumulativeTransactionCount =
    currLatestData.cumulativeConfusionMatrix && currLatestData.cumulativeConfusionMatrix.getAllPositive();
  const cumulativeAssumedTransactionCount =
    currLatestData.cumulativeAssumedConfusionMatrix && currLatestData.cumulativeAssumedConfusionMatrix.getAllPositive();
  const countDiff =
    (cumulativeTransactionCount - cumulativeAssumedTransactionCount) / (cumulativeAssumedTransactionCount || 1);
  if (
    cumulativeTransactionCount !== undefined &&
    !isNaN(countDiff) &&
    Math.abs(countDiff) > PERFORMANCE_ALERT_THRESHOLDS.TRANSACTION_RATE
  ) {
    const transactionWarning = {
      type: ALERT_TYPES.TRANSACTION_COUNT,
      time: timeDifference.percentage,
      value: countDiff
    };
    retMonitorWarnings.push(transactionWarning);
  }
  const cumulativeConversionRate =
    currLatestData.cumulativeConfusionMatrix && getConversionRate(currLatestData.cumulativeConfusionMatrix);
  const cumulativeAssumedConversionRate =
    currLatestData.cumulativeAssumedConfusionMatrix &&
    getConversionRate(currLatestData.cumulativeAssumedConfusionMatrix);

  const rateDiff = cumulativeConversionRate - cumulativeAssumedConversionRate;
  if (
    cumulativeConversionRate !== undefined &&
    !isNaN(rateDiff) &&
    Math.abs(rateDiff) > PERFORMANCE_ALERT_THRESHOLDS.CONVERSION_RATE
  ) {
    const conversionWarning = {
      type: ALERT_TYPES.SUCCESS_RATE,
      time: timeDifference.percentage,
      value: rateDiff,
      isPercentage: true
    };
    retMonitorWarnings.push(conversionWarning);
  }

  return retMonitorWarnings;
}

const MONITOR_KEYS = {
  1: {
    TRUE_POSITIVE: "true-positive",
    FALSE_POSITIVE: "false-positive",
    FALSE_NEGATIVE: "false-negative",
    TRUE_NEGATIVE: "true-negative",
    COUNT: "count",
    OVER_SUM: "over-sum",
    OVER_COUNT: "over-count",
    UNDER_SUM: "under-sum",
    UNDER_COUNT: "under-count",
    ACTUAL_SUM: "actual_sum",
    PREDICTED_SUM: "predicted_sum"
  },
  2: {
    TRUE_POSITIVE: "tp",
    FALSE_POSITIVE: "fp",
    FALSE_NEGATIVE: "fn",
    TRUE_NEGATIVE: "tn",
    COUNT: "counts",
    COUNT_: "count",
    OVER_SUM: "over_sum",
    OVER_COUNT: "over_count",
    UNDER_SUM: "under_sum",
    UNDER_COUNT: "under_count",
    ACTUAL_SUM: "actual_sum",
    PREDICTED_SUM: "predicted_sum"
  }
};

function getMonitorKey(key, version = 1) {
  const monitorVersion = MONITOR_KEYS[version] || MONITOR_KEYS[1];
  return monitorVersion[key] ? monitorVersion[key] : key;
}

function getMonitorVersion(rawData) {
  return rawData && rawData.data && (rawData.data.version || 1);
}

function getMonitorDataPointer(rawData) {
  return (
    rawData &&
    rawData.data &&
    (rawData.data.monitor_results && getMonitorVersion(rawData) === 2 ? rawData.data.monitor_results : rawData.data)
  );
}

function getHighestCountColumn(monitorData, monitorDataRoot) {
  const monitorVersion = getMonitorVersion(monitorData);
  const retData = {
    column: undefined,
    valueCount: 0,
    count: 0
  };
  Object.keys(monitorDataRoot).forEach((sColumn) => {
    let count = 0;
    const columnKeys = Object.keys(monitorDataRoot[sColumn]);
    columnKeys.forEach((sValue) => {
      count += monitorDataRoot[sColumn][sValue][getMonitorKey("COUNT", monitorVersion)];
    });
    if (count > retData.count || retData.column === undefined) {
      retData.column = sColumn;
      retData.valueCount = columnKeys.length;
      retData.count = count;
    } else if (count === retData.count && columnKeys.length < retData.valueCount) {
      retData.column = sColumn;
      retData.valueCount = retData.count;
    }
  });
  return retData.column;
}

function makeEmptyMonitorData(baseObj, obj) {
  if (typeof obj !== "object") {
    if (typeof obj === "number") {
      return 0;
    } else {
      return obj;
    }
  } else if (Array.isArray(obj)) {
    return obj.map((e, i) => makeEmptyMonitorData(baseObj && baseObj[i], e));
  } else {
    const emptyObj = { ...baseObj };
    Object.keys(obj).forEach((oKey) => {
      const currVal = obj[oKey];
      emptyObj[oKey] = makeEmptyMonitorData(baseObj && baseObj[oKey], currVal);
    });
    return emptyObj;
  }
}

function fillEmptyMonitoringDays(state, projectId, rawMonitor, showHoursOverride, timeDifferenceOverride) {
  if (!rawMonitor || !rawMonitor.length) {
    return rawMonitor;
  }
  const firstMonitor = rawMonitor[0];
  const firstDate = formatDate(firstMonitor.start_date, firstMonitor.end_date, showHoursOverride);
  const emptyMonitor = {
    created_by: { ...firstMonitor.created_by },
    data: {},
    deleted: "NOT_DELETED",
    id: -1,
    project_id: firstMonitor.project_id,
    tenant_id: firstMonitor.tenant_id,
    user: { ...firstMonitor.user }
  };
  const segmentDays = {};
  const datesSeen = {};
  const dateFormatString = getDateFormatString(showHoursOverride);
  const datesRange = {
    earliestDate: timeDifferenceOverride
      ? moment(timeDifferenceOverride.startDate)
      : moment(firstDate, dateFormatString),
    latestDate: timeDifferenceOverride ? moment(timeDifferenceOverride.endDate) : moment(firstDate, dateFormatString)
  };

  // Create an entry in the segmentDays object to keep track of the days we have monitoring data for
  // Also creates the list of segments we have monitoring data for

  rawMonitor.forEach((currMonitor) => {
    if (segmentDays[currMonitor.project_segment_id] === undefined) {
      segmentDays[currMonitor.project_segment_id] = {};
    }
    const currDate = formatDate(currMonitor.start_date, currMonitor.end_date, showHoursOverride);
    // Store the date for the current segment
    if (segmentDays[currMonitor.project_segment_id][currDate] === undefined) {
      segmentDays[currMonitor.project_segment_id][currDate] = [currMonitor.start_date, currMonitor.end_date];
    }
    // Find the earliest and latest days with Monitoring Data
    if (datesSeen[currDate] === undefined) {
      datesSeen[currDate] = [currMonitor.start_date, currMonitor.end_date];
      const cMoment = moment(currDate, dateFormatString);
      if (cMoment.isBefore(datesRange.earliestDate)) {
        datesRange.earliestDate = cMoment;
      } else if (cMoment.isAfter(datesRange.latestDate)) {
        datesRange.latestDate = cMoment;
      }
    }

    // Fill out the emptyMonitor object
    Object.keys(currMonitor.data).forEach((dKey) => {
      if (dKey === "user" || dKey === "version") {
        emptyMonitor.data[dKey] = currMonitor.data[dKey];
      } else if (dKey !== "compliance_results" && dKey !== "row_level_state") {
        emptyMonitor.data[dKey] = makeEmptyMonitorData(emptyMonitor.data[dKey], currMonitor.data[dKey]);
      }
    });
  });

  const filledMonitor = [...rawMonitor];

  Object.keys(segmentDays).forEach((segmentId) => {
    const currSegmentDays = segmentDays[segmentId];

    // fill out the missing days with emptyMonitor objects
    let cMoment = moment(datesRange.earliestDate);
    while (cMoment.isBefore(datesRange.latestDate)) {
      const cDate = cMoment.format(dateFormatString);
      if (currSegmentDays[cDate] === undefined) {
        const newMonitor = JSON.parse(JSON.stringify(emptyMonitor));
        newMonitor.start_date = cMoment.toISOString();
        const endMoment = moment(cMoment).add(1, showHoursOverride ? "hours" : "days");
        newMonitor.project_segment_id = Number(segmentId);
        newMonitor.end_date = endMoment.toISOString();
        newMonitor.created_date = endMoment.toISOString();
        newMonitor.updated_date = endMoment.toISOString();
        filledMonitor.push(newMonitor);
      }
      cMoment.add(1, showHoursOverride ? "hours" : "days");
    }
  });

  // Only look at the latest time period of monitoring data
  return filledMonitor.filter((currMonitor) => {
    const cSegmentId = currMonitor.project_segment_id;
    const segmentTimePeriod = projectReducer.getDeployedSegmentTimePeriod(state, projectId, cSegmentId);
    let timeDiff = {
      count: 1,
      unit: segmentTimePeriod.toLowerCase() + "s"
    };
    let timeMoment = moment(datesRange.latestDate).subtract(timeDiff.count, timeDiff.unit);
    if (moment(currMonitor.start_date).isSameOrBefore(timeMoment)) {
      return false;
    } else {
      return true;
    }
  });
}

function constructPlaceholderMonitoringData(state, projectId) {
  const project = projectReducer.getProject(state, projectId);
  const isContinuous = projectReducer.getIsContinuous(state, projectId);
  const rawSegments = project && project.segments;
  const defaultResults = {};

  const EMPTY_VALUE = isContinuous
    ? {
        counts: 0,
        over_count: 0,
        over_sum: 0,
        under_count: 0,
        under_sum: 0,
        actual_sum: 0,
        predicted_sum: 0
      }
    : {
        fn: 0,
        fp: 0,
        tn: 0,
        tp: 0,
        counts: 0
      };

  if (rawSegments && rawSegments.length > 1) {
    rawSegments.forEach((segment) => {
      if (segment && segment.conditions) {
        segment.conditions.forEach((cond) => {
          if (cond) {
            if (defaultResults[cond.column] === undefined) {
              defaultResults[cond.column] = {};
            }
            defaultResults[cond.column][cond.value] = { ...EMPTY_VALUE };
          }
        });
      }
    });
  } else {
    const jobIds = projectReducer.getAllJobIdsFromOptimizer(state, projectId);
    let filled = false;
    jobIds.forEach((jobId) => {
      const outcome = setupReducer.getOutcomeFromDataset(state, jobId);
      const successOptions = setupReducer.getSuccessOptions(state, jobId);
      if (outcome !== undefined && successOptions && successOptions.length) {
        if (defaultResults[outcome] === undefined) {
          defaultResults[outcome] = {};
        }
        successOptions.forEach((sOption) => {
          if (sOption && sOption.value !== undefined) {
            defaultResults[outcome][sOption.value] = { ...EMPTY_VALUE };
            filled = true;
          }
        });
      }
    });
    if (filled === false) {
      defaultResults["{Missing}"] = {
        ["{Missing]"]: { ...EMPTY_VALUE }
      };
    }
  }

  return defaultResults;
}

export function parseMonitoring(state, projectId, data, saveUserData = false, timeDifferenceOverride) {
  if (!data) {
    return;
  }

  // Show time if monitoring time is less than a day
  let showHoursOverride = false;

  // Fill in missing monitoring data (version 2 only) and check if `showHoursOverride` should be true
  const rawMonitor = data.map((currMonitor) => {
    if (moment(currMonitor.end_date).diff(currMonitor.start_date, "hours") <= 2) {
      showHoursOverride = true;
    }

    const currData = currMonitor;
    if (getMonitorVersion(currData) === 2) {
      const currResults = getMonitorDataPointer(currData);
      if (!currResults || !Object.keys(currResults).length) {
        if (!currData.data) {
          currData.data = {};
        }
        currData.data.monitor_results = constructPlaceholderMonitoringData(state, projectId);
      }
    }
    let retCurrData = currData;
    // Check for invalid column names where spaces got replaced with underscores
    if (currData?.data?.monitor_results) {
      const retMonitorResults = {};
      const overallRunId = projectReducer.getOverallRunId(state, projectId);
      const datasetId = setupReducer.getDatasetId(state, overallRunId);
      const dataset = homeReducer.getDataset(state, datasetId);
      const problematicLabels = dataset?.labels?.filter((l) => String(l).includes(" ")) ?? [];
      const datasetFields = dataset?.fields ?? {};
      Object.keys(currData.data.monitor_results).forEach((cKey) => {
        let retCol = cKey;
        if (!datasetFields[cKey]) {
          const newColKey = problematicLabels.find((nKey) => nKey.replace(/ /g, "_") === cKey);
          if (newColKey !== undefined) {
            retCol = newColKey;
          }
        }
        const cValues = currData.data.monitor_results[cKey];
        retMonitorResults[retCol] = cValues;
      });

      retCurrData = {
        ...currData,
        data: {
          ...currData.data,
          monitor_results: retMonitorResults
        }
      };
    }
    return retCurrData;
  });
  let user = undefined;
  if (saveUserData) {
    user = rawMonitor && rawMonitor[0] && rawMonitor[0].user;
  }
  const project = projectReducer.getProject(state, projectId);
  const isContinuous = projectReducer.getIsContinuous(state, projectId);
  const rawSegments = project && project.segments;
  const rawScenarios = project && project.settings && project.settings.scenarios;
  const rawOriginalScenarios = project && project.settings && project.settings.originalScenarios;

  const segmentConfusionMatrices = project && project.settings && project.settings.segmentConfusionMatrices;

  const monitorData = {};
  const monitorWarnings = {};

  const projectSegments = {};

  let overallSegmentId;
  const otherSegments = [];

  const dateObj = {};

  if (!rawSegments) {
    return;
  }

  rawSegments.forEach((segment, i, sList) => {
    if (segment && segment.conditions && !segment.conditions.length) {
      overallSegmentId = segment.id;
      if (sList.length === 1) {
        otherSegments.push(segment.id);
      }
    } else {
      otherSegments.push(segment.id);
    }
    projectSegments[segment.id] = segment;
    monitorData[segment.id] = {
      data: [],
      scenarios: [],
      segmentId: segment.id,
      hasSufficientData: false
    };
  });

  // Fill empty days
  const filledMonitor = fillEmptyMonitoringDays(
    state,
    projectId,
    rawMonitor,
    showHoursOverride,
    timeDifferenceOverride
  ).sort((m1, m2) => {
    return moment(m1.start_date).isBefore(m2.start_date) ? -1 : 1;
  });

  let timeDifference;
  if (timeDifferenceOverride) {
    timeDifference = timeDifferenceOverride;
  } else {
    const timePeriod = projectReducer.getDeployedSegmentTimePeriod(state, projectId);

    filledMonitor.forEach((currMonitor) => {
      let startDate = currMonitor.start_date;
      let endDate = currMonitor.end_date;

      let startDateMoment = moment(startDate);
      let endDateMoment = moment(endDate);
      if (timeDifference) {
        if (startDateMoment.isAfter(timeDifference.startDate)) {
          startDate = timeDifference.startDate;
          startDateMoment = moment(startDate);
        }
        if (endDateMoment.isBefore(timeDifference.endDate)) {
          endDate = timeDifference.endDate;
          endDateMoment = moment(endDate);
        }
      }

      const dateDiff = endDateMoment.diff(startDateMoment, "days");

      const daysInTimePeriod = moment(startDateMoment)
        .add(1, timePeriod.toLowerCase() + "s")
        .diff(startDateMoment, "days");

      if (timeDifference === undefined || dateDiff > timeDifference.daysDifference) {
        timeDifference = {
          ...timeDifference,
          startDate: startDate,
          endDate: endDate,
          daysDifference: dateDiff,
          daysInTimePeriod: daysInTimePeriod,
          percentage: dateDiff / (daysInTimePeriod || 1),
          scalar: 1 / (daysInTimePeriod || 1)
        };
      }
    });
  }

  // Split and parse each monitor
  filledMonitor.forEach((currMonitor) => {
    const segmentId = currMonitor.project_segment_id;
    const currSegment = projectSegments[segmentId];

    const assumptions = currSegment && currSegment.assumption && currSegment.assumption.data;
    let conditions = currSegment && currSegment.conditions;

    let currLabel = currSegment && currSegment.name;
    const currDate = formatDate(currMonitor.start_date, currMonitor.end_date, showHoursOverride);

    if (!conditions || !assumptions || (rawSegments.length > 1 && !conditions.length)) {
      return;
    }

    if (dateObj[currDate] !== undefined) {
      if (dateObj[currDate][segmentId] != undefined) {
        dateObj[currDate][segmentId] += 1;
      } else {
        dateObj[currDate][segmentId] = 1;
      }
    } else {
      dateObj[currDate] = {
        [segmentId]: 1
      };
    }
    const currMonitorRoot = getMonitorDataPointer(currMonitor);

    if (rawSegments.length === 1 && !conditions.length && currMonitorRoot) {
      const selColumn = getHighestCountColumn(currMonitor, currMonitorRoot);
      conditions = Object.keys(currMonitorRoot[selColumn]).map((sValue) => {
        return {
          column: selColumn,
          value: sValue
        };
      });
      currLabel = DEFAULT_OVERALL_LABEL;
    }

    const currMonitorDataArr = conditions
      .map((cond) => {
        const segmentColumn = cond.column;
        const segmentValue = cond.value;
        return currMonitorRoot && currMonitorRoot[segmentColumn] && currMonitorRoot[segmentColumn][segmentValue];
      })
      .filter((elem) => elem !== undefined);

    if (!currMonitorDataArr.length) {
      return;
    }

    const retMonitorPoint = generateMonitoringPoint(state, {
      currMonitor,
      currMonitorDataArr,
      currLabel,
      currDate,
      segmentConfusionMatrices,
      isContinuous,
      projectId,
      segmentId,
      assumptions,
      conditions,
      timeDifference
    });

    monitorData[segmentId].data.push(retMonitorPoint);
    monitorData[segmentId].hasSufficientData =
      monitorData[segmentId].hasSufficientData || currMonitor.data.has_sufficient_data;
  });

  // Create date array for iteration and time object for reference
  for (let cDate in dateObj) {
    let count = 0;
    for (let sId in dateObj[cDate]) {
      count = Math.max(dateObj[cDate][sId], count);
    }
    dateObj[cDate] = count;
  }

  let dateArr = [];
  for (let cDate in dateObj) {
    for (let i = 0; i < dateObj[cDate]; i++) {
      dateArr.push(cDate);
    }
  }

  // Aggregate for Overall
  if (rawSegments.length > 1) {
    const datesSeen = {};
    const overallData = dateArr.map((date, i) => {
      let dateOffset = 0;
      if (datesSeen[date] !== undefined) {
        dateOffset = datesSeen[date];
        datesSeen[date] += 1;
      } else {
        datesSeen[date] = 1;
      }
      return generateOverallMonitoringPoint({
        date: date,
        overallMonitorPoint: monitorData[overallSegmentId].data[i],
        projectSegments,
        otherSegments,
        monitorData,
        overallSegmentId,
        isContinuous,
        dateOffset
      });
    });
    monitorData[overallSegmentId].data = overallData;
  }

  // Generate Cumulative Values
  Object.keys(monitorData).forEach((sId) => {
    const cSeg = monitorData[sId];
    let accMatrix = new ConfusionMatrix(0, 0, 0, 0);
    let accAssumedMatrix = new ConfusionMatrix(0, 0, 0, 0);
    let cumulativeCounts = {
      count: 0,
      cost: 0,
      underCount: 0,
      overCount: 0,
      underCost: 0,
      overCost: 0
    };
    let cumulativeAssumedCount = 0;
    let cumulativeAssumedCost = 0;
    cSeg.data =
      cSeg.data &&
      cSeg.data.map((cData) => {
        const currData = { ...cData };
        if (!isContinuous) {
          const cMatrix = currData.confusionMatrix;
          currData.cumulativeConfusionMatrix = accMatrix.combineMatrices(cMatrix);
          accMatrix = accMatrix.combineMatrices(cMatrix);
          const assumedCMatrix = currData.assumedConfusionMatrix;
          currData.cumulativeAssumedConfusionMatrix = accAssumedMatrix.combineMatrices(assumedCMatrix);
          accAssumedMatrix = accAssumedMatrix.combineMatrices(assumedCMatrix);

          currData.cumulativeAssumedCount = Math.round(currData.cumulativeAssumedConfusionMatrix.getTotal());
          currData.cumulativeAssumedTransactionCount = Math.round(
            currData.cumulativeAssumedConfusionMatrix.getAllPositive()
          );
          currData.cumulativeCount = Math.round(currData.cumulativeConfusionMatrix.getTotal());
          currData.cumulativeTransactionCount = Math.round(currData.cumulativeConfusionMatrix.getAllPositive());
          currData.cumulativeAssumedConversionRate = getConversionRate(currData.cumulativeAssumedConfusionMatrix);
          currData.cumulativeConversionRate = getConversionRate(currData.cumulativeConfusionMatrix);
        } else {
          cumulativeCounts.count = cumulativeCounts.count + currData.count;
          cumulativeCounts.cost = cumulativeCounts.cost + currData.cost;
          cumulativeCounts.underCount = cumulativeCounts.underCount + currData.underCount;
          cumulativeCounts.overCount = cumulativeCounts.overCount + currData.overCount;
          cumulativeCounts.underCost = cumulativeCounts.underCost + currData.underCost;
          cumulativeCounts.overCost = cumulativeCounts.overCost + currData.overCost;
          cumulativeAssumedCount = cumulativeAssumedCount + currData.countAssumed;
          cumulativeAssumedCost = cumulativeAssumedCost + currData.costAssumed;
          currData.cumulativeCount = cumulativeCounts.count;
          currData.cumulativeCost = cumulativeCounts.cost;
          currData.cumulativeUnderCount = cumulativeCounts.underCount;
          currData.cumulativeOverCount = cumulativeCounts.overCount;
          currData.cumulativeUnderCost = cumulativeCounts.underCost;
          currData.cumulativeOverCost = cumulativeCounts.overCost;
          currData.cumulativeAssumedCount = cumulativeAssumedCount;
          currData.cumulativeAssumedCost = cumulativeAssumedCost;
        }
        return currData;
      });
  });

  // Calculate Scenarios
  const scenariosBySegment = {};
  rawScenarios.forEach((scenario, i) => {
    Object.keys(scenario).forEach((scenarioKey) => {
      const currScenarioSegment = scenario[scenarioKey];
      if (scenariosBySegment[currScenarioSegment.project_segment_id]) {
        scenariosBySegment[currScenarioSegment.project_segment_id][i] = currScenarioSegment;
      } else {
        scenariosBySegment[currScenarioSegment.project_segment_id] = [currScenarioSegment];
      }
    });
  });

  const scenarioIndex = getIsMonitoringDemo()
    ? projectReducer.getViewingScenarioIndex(state, projectId)
    : projectReducer.getDeployedScenarioIndex(state, projectId);

  rawSegments.forEach((segment) => {
    const segmentId = segment.id;
    const segmentData = monitorData[segmentId].data;
    const scenarioData = scenariosBySegment[segmentId] || [];

    const datesSeen = {};
    const scenarios = dateArr.map((date, i) => {
      let dateOffset = 0;
      if (datesSeen[date] !== undefined) {
        dateOffset = datesSeen[date];
        datesSeen[date] += 1;
      } else {
        datesSeen[date] = 1;
      }

      const currSegmentData = findWithOffset(segmentData, (elem) => elem.date === date, dateOffset);

      return generateScenarioPoint({
        state,
        date,
        currSegmentData: currSegmentData,
        scenarioData,
        rawOriginalScenarios,
        scenarioIndex,
        isContinuous,
        dateOffset,
        timeDifference,
        segmentId,
        projectId
      });
    });

    monitorData[segmentId].scenarios = scenarios.filter((e) => Boolean(e));
  });

  // Aggregate Scenarios into Overall
  if (rawSegments.length > 1) {
    const datesSeen = {};
    const overallScenarios = dateArr.map((date, i) => {
      let dateOffset = 0;
      if (datesSeen[date] !== undefined) {
        dateOffset = datesSeen[date];
        datesSeen[date] += 1;
      } else {
        datesSeen[date] = 1;
      }

      const currOverallScenarioPoint = findWithOffset(
        monitorData[overallSegmentId].scenarios,
        (elem) => elem.date === date,
        dateOffset
      );

      return generateOverallScenarioPoint({
        otherSegments,
        monitorData,
        date,
        scenarioIndex,
        isContinuous,
        currOverallScenarioPoint: currOverallScenarioPoint,
        dateOffset,
        overallSegmentId
      });
    });

    monitorData[overallSegmentId].scenarios = overallScenarios;
  }

  const latestData = {};
  const latestScenario = {};
  Object.keys(monitorData).forEach((currSegmentId) => {
    const currData = monitorData[currSegmentId].data;
    const currScenario = monitorData[currSegmentId].scenarios;
    latestData[currSegmentId] = currData[currData.length - 1];
    latestScenario[currSegmentId] = currScenario[currScenario.length - 1];
  });

  Object.keys(monitorData).forEach((currSegmentId) => {
    const currLatestData = latestData[currSegmentId];
    if (!currLatestData) {
      return;
    }

    const currMonitorWarnings = generateMonitoringWarnings(state, projectId, currSegmentId, {
      currLatestData,
      timeDifference,
      isContinuous
    });

    monitorWarnings[currSegmentId] = currMonitorWarnings;
  });

  const retMonitor = {
    ...DEFAULT_MONITOR,
    userFeedback: [],
    data: monitorData,
    warnings: monitorWarnings,
    latestData: latestData,
    latestScenario: latestScenario,
    timeDifference: timeDifference,
    user: user
  };

  return retMonitor;
}

export function constructPlotFromData(segmentData) {
  if (!segmentData || !isEnabled("user-compliance")) {
    return;
  }

  let hasData = false;

  const retData = segmentData.data.map((currData) => {
    const emptyData = {
      label: currData.label,
      date: currData.date
    };
    if (currData.compliance) {
      hasData = true;
      return {
        ...emptyData,
        complied: currData.compliance.tp + currData.compliance.tn,
        notComplied: currData.compliance.fp + currData.compliance.fn,

        compliedAct: currData.compliance.tp,
        compliedNotAct: currData.compliance.tn,

        notCompliedNotAct: currData.compliance.fn,
        notCompliedAct: currData.compliance.fp,

        actComplianceRate: currData.compliance.tp / (currData.compliance.tp + currData.compliance.fn || 1),
        notActComplianceRate: currData.compliance.tn / (currData.compliance.tn + currData.compliance.fp || 1)
      };
    } else {
      return emptyData;
    }
  });

  return hasData ? retData : undefined;
}

export function getCompliancePlot(state, projectId, segmentId, userId) {
  let monitor = getMonitor(state, projectId);
  if (!isNaN(userId)) {
    monitor = monitor && monitor.userMonitors && monitor.userMonitors[userId];
  }
  const retSegmentId = segmentId ? segmentId : projectReducer.getSegmentId(state, projectId);
  const segmentData = monitor && monitor.data && monitor.data[retSegmentId];

  const compliancePlot = constructPlotFromData(segmentData);

  return compliancePlot;
}

export function getHasUserComplianceData(state, projectId, segmentIdOverride, userIdOverride) {
  if (!isEnabled("user-compliance")) {
    return false;
  }
  const userData = getUserData(state, projectId);
  const users = userData
    ? Object.keys(userData).filter((u) => (userIdOverride !== undefined ? Number(u) === Number(userIdOverride) : true))
    : [];
  const segmentId = segmentIdOverride ? segmentIdOverride : projectReducer.getSegmentId(state, projectId);
  for (let userIndex = 0; userIndex < users.length; userIndex++) {
    const currUser = users[userIndex];
    const currUserData = userData[currUser] && userData[currUser].data;
    const currSegmentData = currUserData[segmentId] ? currUserData[segmentId].data : [];
    for (let segmentIndex = 0; segmentIndex < currSegmentData.length; segmentIndex++) {
      const currData = currSegmentData[segmentIndex];
      if (currData && currData.compliance) {
        return true;
      }
    }
  }
  return false;
}

export function getFairnessPlot(state, projectId) {
  let monitor = getMonitor(state, projectId);

  if (!monitor) {
    return [];
  }

  const overallRunId = projectReducer.getOverallRunId(state, projectId);

  const fairnessObjective = setupReducer.getFairnessObjective(state, overallRunId);

  if (fairnessObjective === FAIRNESS_OBJECTIVE.NONE) {
    return [];
  }

  const fairnessToleranceBound = setupReducer.getFairnessToleranceBound(state, overallRunId);

  const setup = setupReducer.getSetup(state, overallRunId);

  const datasetId = setupReducer.getDatasetId(state, overallRunId);
  const outcomeColumn = homeReducer.getOutcome(state, datasetId);
  const successValue = setup?.success?.label;

  const splitColumn = setupReducer.getSplitColumn(state, overallRunId);
  // const splitColumn = "Region";
  // const dataset = homeReducer.getDataset(state, datasetId);
  // const splitValue = dataset?.fields[splitColumn]?.data[0]?.value
  // const splitValue = "East";

  const modelGroups = projectReducer.getModelGroups(state, projectId);
  const drilldownValues = modelGroups?.drilldownValues?.filter(
    (drilldown, i, drilldownValues) => drilldown?.value !== "Overall" || drilldownValues.length <= 1
  );

  const monitorLength = drilldownValues.reduce((acc, currDrilldown) => {
    const currSegmentId = currDrilldown.segmentId;
    const currLength = monitor?.data && monitor?.data[currSegmentId]?.data?.length;

    return Math.max(acc, currLength);
  }, 0);

  const monitorData = monitor.data;

  const retFairnessPlot = [];
  const cumulative = {
    counts: {},
    values: {}
  };
  for (let monitorIndex = 0; monitorIndex < monitorLength; monitorIndex++) {
    const currFairnessPoint = {
      colors: {},
      labels: {},
      segmentId: []
    };
    drilldownValues.forEach((drilldown) => {
      const segmentId = drilldown.segmentId;
      const splitValue = drilldown.value;
      currFairnessPoint.segmentId.push(segmentId);
      currFairnessPoint.colors[segmentId] = drilldown.color;
      currFairnessPoint.labels[segmentId] = drilldown.label;
      const currData =
        monitorData[segmentId] && monitorData[segmentId].data && monitorData[segmentId].data[monitorIndex];

      if (!currData) {
        return;
      }

      if (!currFairnessPoint.date) {
        currFairnessPoint.date = currData.date;
      }

      if (fairnessObjective === FAIRNESS_OBJECTIVE.TRUE_POSITIVE_RATE) {
        const currStats = currData?.confusionMatrix;

        const currCount = (currStats?.tp || 0) + (currStats?.fp || 0);
        const currValue = (currStats?.tp || 0) / (currCount || 1);

        currFairnessPoint[`${segmentId}_value`] = currValue;
        currFairnessPoint[`${segmentId}_count`] = currCount;

        const prevCumulativeValue = cumulative.values?.[segmentId] || 0;
        const prevCumulativeCount = cumulative.counts?.[segmentId] || 0;

        const newCumulativeCount = prevCumulativeCount + currCount;
        const newCumulativeValue =
          (prevCumulativeValue * prevCumulativeCount) / (newCumulativeCount || 1) +
          (currValue * currCount) / (newCumulativeCount || 1);

        cumulative.values[segmentId] = newCumulativeValue;
        cumulative.counts[segmentId] = newCumulativeCount;

        if (prevCumulativeCount > 0 || currCount > 0) {
          currFairnessPoint[`${segmentId}_value_cumulative`] = newCumulativeValue;
          currFairnessPoint[`${segmentId}_count_cumulative`] = newCumulativeCount;
        }
      } else if (fairnessObjective === FAIRNESS_OBJECTIVE.FALSE_POSITIVE_RATE) {
        const currStats = currData?.confusionMatrix;

        const currCount = (currStats?.tp || 0) + (currStats?.fp || 0);
        const currValue = (currStats?.fp || 0) / (currCount || 1);

        currFairnessPoint[`${segmentId}_value`] = currValue;
        currFairnessPoint[`${segmentId}_count`] = currCount;

        const prevCumulativeValue = cumulative.values?.[segmentId] || 0;
        const prevCumulativeCount = cumulative.counts?.[segmentId] || 0;

        const newCumulativeCount = prevCumulativeCount + currCount;
        const newCumulativeValue =
          (prevCumulativeValue * prevCumulativeCount) / (newCumulativeCount || 1) +
          (currValue * currCount) / (newCumulativeCount || 1);

        cumulative.values[segmentId] = newCumulativeValue;
        cumulative.counts[segmentId] = newCumulativeCount;

        currFairnessPoint[`${segmentId}_value_cumulative`] = newCumulativeValue;
        currFairnessPoint[`${segmentId}_count_cumulative`] = newCumulativeCount;
      } else if (fairnessObjective === FAIRNESS_OBJECTIVE.ODDS) {
        const currStats = currData?.confusionMatrix;

        const currCount = (currStats?.tp || 0) + (currStats?.fp || 0) + (currStats?.tn || 0) + (currStats?.fn || 0);
        const currValue = ((currStats?.tp || 0) + (currStats?.fp || 0)) / (currCount || 1);

        currFairnessPoint[`${segmentId}_value`] = currValue;
        currFairnessPoint[`${segmentId}_count`] = currCount;

        const prevCumulativeValue = cumulative.values?.[segmentId] || 0;
        const prevCumulativeCount = cumulative.counts?.[segmentId] || 0;

        const newCumulativeCount = prevCumulativeCount + currCount;
        const newCumulativeValue =
          (prevCumulativeValue * prevCumulativeCount) / (newCumulativeCount || 1) +
          (currValue * currCount) / (newCumulativeCount || 1);

        cumulative.values[segmentId] = newCumulativeValue;
        cumulative.counts[segmentId] = newCumulativeCount;

        if (prevCumulativeCount > 0 || currCount > 0) {
          currFairnessPoint[`${segmentId}_value_cumulative`] = newCumulativeValue;
          currFairnessPoint[`${segmentId}_count_cumulative`] = newCumulativeCount;
        }
      } else if (fairnessObjective === FAIRNESS_OBJECTIVE.ACCURACY) {
        const currStats = currData?.confusionMatrix;

        const currCount = (currStats?.tp || 0) + (currStats?.fp || 0) + (currStats?.tn || 0) + (currStats?.fn || 0);
        const currValue = ((currStats?.tp || 0) + (currStats?.tn || 0)) / (currCount || 1);

        currFairnessPoint[`${segmentId}_value`] = currValue;
        currFairnessPoint[`${segmentId}_count`] = currCount;

        const prevCumulativeValue = cumulative.values?.[segmentId] || 0;
        const prevCumulativeCount = cumulative.counts?.[segmentId] || 0;

        const newCumulativeCount = prevCumulativeCount + currCount;
        const newCumulativeValue =
          (prevCumulativeValue * prevCumulativeCount) / (newCumulativeCount || 1) +
          (currValue * currCount) / (newCumulativeCount || 1);

        cumulative.values[segmentId] = newCumulativeValue;
        cumulative.counts[segmentId] = newCumulativeCount;

        if (prevCumulativeCount > 0 || currCount > 0) {
          currFairnessPoint[`${segmentId}_value_cumulative`] = newCumulativeValue;
          currFairnessPoint[`${segmentId}_count_cumulative`] = newCumulativeCount;
        }
      }
    });
    retFairnessPlot.push(currFairnessPoint);
  }

  return retFairnessPlot;
}

export function getfairnessWarnings(state, projectId, fairnessPlotOverride = undefined) {
  const fairnessPlot = fairnessPlotOverride ? fairnessPlotOverride : getFairnessPlot(state, projectId);

  if (!fairnessPlot || !fairnessPlot?.length) {
    return [];
  }

  const overallRunId = projectReducer.getOverallRunId(state, projectId);

  const fairnessObjective = setupReducer.getFairnessObjective(state, overallRunId);
  const fairnessToleranceBound = setupReducer.getFairnessToleranceBound(state, overallRunId);

  const reversedPlot = fairnessPlot?.map((e) => e).reverse() ?? [];

  const latestFairnessMetric = {
    labels: {},
    colors: {},
    segmentId: reversedPlot[0]?.segmentId
  };

  latestFairnessMetric.segmentId?.forEach((sId) => {
    const latestValidMonitor = reversedPlot.find((fMon) => fMon[`${sId}_value_cumulative`] !== undefined);
    latestFairnessMetric.labels[sId] = latestValidMonitor?.labels?.[sId];
    latestFairnessMetric.colors[sId] = latestValidMonitor?.colors?.[sId];
    latestFairnessMetric[`${sId}_value_cumulative`] = latestValidMonitor?.[`${sId}_value_cumulative`];
    latestFairnessMetric[`${sId}_date`] = latestValidMonitor?.date;
  });

  const segmentIds = latestFairnessMetric.segmentId;

  if (
    fairnessObjective === FAIRNESS_OBJECTIVE.TRUE_POSITIVE_RATE ||
    fairnessObjective === FAIRNESS_OBJECTIVE.FALSE_POSITIVE_RATE ||
    fairnessObjective === FAIRNESS_OBJECTIVE.ODDS ||
    fairnessObjective === FAIRNESS_OBJECTIVE.ACCURACY
  ) {
    const fairnessValues = segmentIds.map((sId) => latestFairnessMetric[`${sId}_value_cumulative`]);

    const warningSegIds = {};

    let validSegIds = [...segmentIds];

    while (
      !validSegIds.every((sId_A) => {
        return validSegIds.every((sId_B) => {
          if (sId_A === sId_B) {
            return true;
          }
          return (
            Math.abs(
              (latestFairnessMetric[`${sId_B}_value_cumulative`] ?? 0) -
                (latestFairnessMetric[`${sId_A}_value_cumulative`] ?? 0)
            ) <=
            fairnessToleranceBound / 100
          );
        });
      })
    ) {
      const targetValue =
        validSegIds
          .map((sId) => latestFairnessMetric[`${sId}_value_cumulative`])
          .reduce((acc, elem) => acc + (elem || 0), 0) / segmentIds.length;

      validSegIds.sort((sId_A, sId_B) => {
        const aValue = latestFairnessMetric[`${sId_A}_value_cumulative`] ?? 0;
        const bValue = latestFairnessMetric[`${sId_B}_value_cumulative`] ?? 0;

        return Math.abs(aValue - targetValue) - Math.abs(bValue - targetValue);
      });
      const warningId = validSegIds.pop();
      warningSegIds[warningId] = true;
    }

    const warnings = segmentIds.map((sId) => {
      const retWarning = {
        segmentId: sId,
        label: latestFairnessMetric.labels[sId],
        color: latestFairnessMetric.colors[sId],
        value: latestFairnessMetric[`${sId}_value_cumulative`] ?? 0,
        count: latestFairnessMetric[`${sId}_count_cumulative`] ?? 0,
        date: latestFairnessMetric[`${sId}_date`]
      };

      if (warningSegIds[sId]) {
        retWarning.warning = true;
      }
      return retWarning;
    });

    return warnings;
  }

  return [];
}

function formatPercentage(num, formatOverride) {
  const strNum = numeral(num).format(formatOverride ?? "0.[00]");
  return Number(strNum);
}

export function getDataDrift(state, projectId) {
  let monitor = getMonitor(state, projectId);

  if (!monitor) {
    return {};
  }

  const nextId = nextReducer.getNextIdFromProjectId(state, projectId);

  const overallRunId = projectReducer.getOverallRunId(state, projectId);
  const overallSegmentId = projectReducer.getOverallSegmentId(state, projectId);

  const datasetId = setupReducer.getDatasetId(state, overallRunId);

  const rankedFields = homeReducer.getRankedFields(state, datasetId);

  const filters = {};
  // const segmentId = projectReducer.getSegmentId(state, projectId);
  // const segment = projectReducer.getSegment(state, projectId, segmentId);
  // segment?.conditions?.forEach(cond => {
  //   const cCol = cond?.column;
  //   const cVal = cond?.value;
  //   filters[cCol] = cVal;
  // })

  const insights = nextReducer.getRelatedInsights(
    state,
    nextId,
    undefined,
    filters,
    undefined,
    undefined,
    {
      limit: rankedFields.length
    },
    false
  );

  const datasetDistribution = {};
  insights.forEach((insight) => {
    const column = insight?.title;
    datasetDistribution[column] = {};
    const chart = insight?.chart?.data;
    chart.forEach((cData) => {
      const columnValue = cData.name;
      const winPercent = cData.y;
      datasetDistribution[column][columnValue] = winPercent;
    });
  });

  const totals = {};

  const monitoringDistributionData = [];

  monitor?.data?.[overallSegmentId]?.data?.forEach((mData) => {
    const retDistributionData = {
      date: mData?.date,
      data: {}
    };
    for (const [col, cData] of Object.entries(mData?.rawData ?? {})) {
      if (!retDistributionData.data[col]) {
        retDistributionData.data[col] = {};
      }
      if (!totals[col]) {
        totals[col] = {};
      }
      for (const [val, vData] of Object.entries(cData ?? {})) {
        const winPercentage = (vData?.tp ?? 0) / (vData?.count ?? 1);
        retDistributionData.data[col][val] = formatPercentage(winPercentage * 100);
        if (!totals[col][val]) {
          totals[col][val] = {
            tp: 0,
            count: 0
          };
        }
        totals[col][val].tp += vData?.tp ?? 0;
        totals[col][val].count += vData?.count ?? 0;
      }
    }

    monitoringDistributionData.push(retDistributionData);
  });

  const monitoringDistribution = {};
  for (const [col, cData] of Object.entries(totals)) {
    if (!monitoringDistribution[col]) {
      monitoringDistribution[col] = {};
    }
    for (const [val, vData] of Object.entries(cData ?? {})) {
      const winPercentage = (vData?.tp ?? 0) / (vData?.count ?? 1);
      monitoringDistribution[col][val] = formatPercentage(winPercentage * 100);
    }
  }

  const rankedChanges = [];
  for (const [col, cData] of Object.entries(totals)) {
    for (const [val, vData] of Object.entries(cData ?? {})) {
      const currEntry = {
        label: col + ": " + val,
        column: col,
        value: val
      };
      const cData = datasetDistribution?.[col]?.[val] ?? 0;
      const winPercentage = (vData?.tp ?? 0) / (vData?.count ?? 1);
      const cMonitor = formatPercentage(winPercentage * 100);
      currEntry.datasetDistribution = cData;
      currEntry.monitoringDistribution = cMonitor;
      const rawChange = (cMonitor - cData) / (cData ?? 1);
      const change = formatPercentage(rawChange * 100);
      currEntry.change = change;
      rankedChanges.push(currEntry);
    }
  }

  rankedChanges.sort((c1, c2) => {
    return Math.abs(c2.change) - Math.abs(c1.change);
  });

  return {
    datasetDistribution: datasetDistribution,
    monitoringDistribution: monitoringDistribution,
    monitoringDistributionData: monitoringDistributionData,
    rankedChanges: rankedChanges
  };
}

function getConfusionMatrixData(cm) {
  if (!cm) {
    return {};
  }
  return {
    "True Positive Count": Math.round(cm.getTruePositive()),
    "False Positive Count": Math.round(cm.getFalsePositive()),
    "True Negative Count": Math.round(cm.getTrueNegative()),
    "False Negative Count": Math.round(cm.getFalseNegative()),
    Recall: formatPercentage(cm.getRecall(), "0.[00000]"),
    Precision: formatPercentage(cm.getPrecision(), "0.[00000]"),
    Accuracy: formatPercentage(cm.getAccuracy(), "0.[00000]"),
    "F1 Score": formatPercentage(cm.getF1Score(), "0.[00000]"),
    MCC: formatPercentage(cm.getMatthewsCorrelationCoefficient(), "0.[00000]")
  };
}

export function getModelDrift(state, projectId) {
  let monitor = getMonitor(state, projectId);

  if (!monitor) {
    return {};
  }

  const nextId = nextReducer.getNextIdFromProjectId(state, projectId);

  const overallRunId = projectReducer.getOverallRunId(state, projectId);
  const overallSegmentId = projectReducer.getOverallSegmentId(state, projectId);

  const setupLabels = setupReducer.getSetupLabels(state, overallRunId);

  const datasetId = setupReducer.getDatasetId(state, overallRunId);

  const segmentId = overallSegmentId;

  const expectedValues = {
    profit: 0,
    cost: 0,
    revenue: 0
  };
  const monitoringValues = {
    profit: 0,
    cost: 0,
    revenue: 0
  };
  const monitoringValuesData = [];

  const monitorData = monitor?.data?.[segmentId];
  monitorData?.data?.forEach((mData, i) => {
    const currScenarioData = monitorData?.scenarios?.[i]?.selectedScenario;

    const profitAssumed = currScenarioData?.profitAssumed ?? 0;
    const costAssumed = currScenarioData?.costAssumed ?? 0;
    const revenueAssumed = currScenarioData?.revenueAssumed ?? 0;

    const profit = currScenarioData?.profit ?? 0;
    const cost = currScenarioData?.cost ?? 0;
    const revenue = currScenarioData?.revenue ?? 0;

    expectedValues.profit += profitAssumed;
    expectedValues.cost += costAssumed;
    expectedValues.revenue += revenueAssumed;
    monitoringValues.profit += profit;
    monitoringValues.cost += cost;
    monitoringValues.revenue += revenue;

    const monitorCM = mData?.confusionMatrix;
    const expectedCM = mData?.assumedConfusionMatrix;

    const monitorCMData = getConfusionMatrixData(monitorCM);
    const expectedCMData = getConfusionMatrixData(expectedCM);

    const retValuesData = {
      date: mData?.date,
      data: {
        expected: {
          profit: profitAssumed,
          cost: costAssumed,
          revenue: revenueAssumed,
          ...expectedCMData
        },
        monitoring: {
          profit: profit,
          cost: cost,
          revenue: revenue,
          ...monitorCMData
        }
      }
    };

    monitoringValuesData.push(retValuesData);
  });

  const finalMonitorDataPoint = monitorData?.data?.[monitorData?.data?.length - 1];
  const expectedTotalCM = finalMonitorDataPoint?.cumulativeAssumedConfusionMatrix;
  const monitoringTotalCM = finalMonitorDataPoint?.cumulativeConfusionMatrix;

  const monitoringTotalCMData = getConfusionMatrixData(monitoringTotalCM);
  const expectedTotalCMData = getConfusionMatrixData(expectedTotalCM);

  const retExpectedValues = {
    ...expectedValues,
    ...expectedTotalCMData
  };

  const retMonitoringValues = {
    ...monitoringValues,
    ...monitoringTotalCMData
  };

  const rankedChanges = [];

  Object.keys(retMonitoringValues).forEach((vKey) => {
    const mValue = retMonitoringValues[vKey];
    const eValue = retExpectedValues[vKey];
    const rawChange = (mValue - eValue) / (eValue ?? 1);
    const retChange = {
      label: vKey,
      metric: vKey,
      monitoringValue: mValue,
      expectedValue: eValue,
      change: formatPercentage(rawChange * 100)
    };
    if (vKey === "profit") {
      retChange.label = setupLabels.profitLabel;
      retChange.prefix = setupLabels.unitType;
    } else if (vKey === "revenue") {
      retChange.label = setupLabels.revenueLabel;
      retChange.prefix = setupLabels.unitType;
    } else if (vKey === "cost") {
      retChange.label = setupLabels.costLabel;
      retChange.prefix = setupLabels.unitType;
    }
    rankedChanges.push(retChange);
  });

  rankedChanges.sort((c1, c2) => {
    return Math.abs(c2.change) - Math.abs(c1.change);
  });

  return {
    expectedValues: retExpectedValues,
    monitoringValues: retMonitoringValues,
    monitoringValuesData: monitoringValuesData,
    rankedChanges: rankedChanges
  };
}

export function getDataDriftWarnings(state, projectId) {
  const nextId = nextReducer.getNextIdFromProjectId(state, projectId);
  const dataDriftWarningThreshold = nextMonitoringReducer.getDataDriftWarningThreshold(state, nextId);
  const dataDrift = getDataDrift(state, projectId);

  let warnings;
  dataDrift.rankedChanges?.forEach((d) => {
    if (Math.abs(d?.change) > Math.abs(dataDriftWarningThreshold)) {
      if (!warnings) {
        warnings = {
          type: ALERT_TYPES.DATA_DRIFT,
          threshold: dataDriftWarningThreshold,
          data: []
        };
      }
      warnings.data.push({
        label: d?.label,
        column: d?.column,
        value: d?.value,
        change: d?.change
      });
    }
  });

  return warnings;
}

export function getModelDriftWarnings(state, projectId) {
  const nextId = nextReducer.getNextIdFromProjectId(state, projectId);
  const modelDriftWarningThreshold = nextMonitoringReducer.getModelDriftWarningThreshold(state, nextId);
  const modelDriftWarningTargets = nextMonitoringReducer.getModelDriftWarningTargets(state, nextId);
  const modelDrift = getModelDrift(state, projectId);

  let warnings;

  modelDrift.rankedChanges?.forEach((d) => {
    if (modelDriftWarningTargets?.find((o) => o.value === d?.metric)) {
      if (Math.abs(d?.change) > Math.abs(modelDriftWarningThreshold)) {
        if (!warnings) {
          warnings = {
            type: ALERT_TYPES.MODEL_DRIFT,
            threshold: modelDriftWarningThreshold,
            data: []
          };
        }
        warnings.data.push({
          metric: d?.metric,
          label: d?.label,
          change: d?.change
        });
      }
    }
  });

  return warnings;
}

export function getMonitoringSummary(state, projectId) {
  const monitor = getMonitor(state, projectId);

  if (!monitor || !monitor.data) {
    return;
  }

  const latestDate = monitor.timeDifference?.endDate;

  const segmentId = projectReducer.getOverallSegmentId(state, projectId);
  const runId = projectReducer.getSegmentRunId(state, projectId, segmentId);

  const businessConstraint = setupReducer.getPrimaryBusinessConstraintType(state, projectId, runId);

  let isCumulative = false;

  let targetKey = businessConstraint || SegmentMetric.Profit;

  const setupLabels = setupReducer.getSetupLabels(state, runId);
  const targetLabel = setupLabels[targetKey + "Label"];
  const unitLabel = setupLabels.unitType;

  let cumulativeVal = 0;

  const cleanedData = monitor.data?.[segmentId]?.data?.map((currData) => {
    if (!currData) {
      return;
    }
    const date = currData.date;
    const currVal = currData[targetKey];
    let retVal = currVal;
    if (isCumulative) {
      cumulativeVal = currVal + cumulativeVal;
      retVal = cumulativeVal;
    }

    return {
      date: date,
      [targetLabel]: retVal
    };
  });

  const retData = {
    latestDate: latestDate,
    label: targetLabel,
    isCumulative: isCumulative,
    data: cleanedData,
    unitLabel: unitLabel
  };

  return retData;
}
